import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';
import { ArrowLeft, CheckCircle2, ClipboardCheck, PartyPopper, Sun, Sunset, Moon, Clock, Mail } from 'lucide-react';
import { useT } from '../studioI18n';
import { useProject } from '../ProjectContext';
import { useStudioAuth } from '../StudioAuthContext';
import { studioApi, apiError } from '../studioApi';
import { Page, MotionButton, stagger, rise } from '../components/Motion';

const Field = ({ label, testId, as: As = 'input', ...props }) => (
  <label className="block">
    <span className="text-xs font-bold text-slate-600 ml-1">{label}</span>
    <As className={`st-input mt-1 ${As === 'textarea' ? 'h-auto py-3 min-h-[6rem] resize-y' : ''}`} data-testid={testId} {...props} />
  </label>
);
const TIME_ICONS = { morning: Sun, afternoon: Sunset, evening: Moon, any: Clock };

const burst = () => {
  const end = Date.now() + 1200;
  const colors = ['#0D9488', '#14B8A6', '#F59E0B', '#0F172A', '#ffffff'];
  (function frame() {
    confetti({ particleCount: 4, angle: 60, spread: 70, origin: { x: 0, y: 0.7 }, colors });
    confetti({ particleCount: 4, angle: 120, spread: 70, origin: { x: 1, y: 0.7 }, colors });
    if (Date.now() < end) requestAnimationFrame(frame);
  }());
};

const Success = ({ reference, title, sub, email }) => {
  const { t } = useT();
  const navigate = useNavigate();
  useEffect(() => { burst(); }, []);
  return (
    <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ type: 'spring', stiffness: 260, damping: 22 }} className="st-glass-strong rounded-[2rem] p-8 sm:p-12 text-center max-w-xl mx-auto relative overflow-hidden" data-testid="estimate-success-celebration">
      <div className="absolute -inset-10 bg-teal-400/15 blur-3xl" />
      <motion.span initial={{ scale: 0, rotate: -30 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: 'spring', stiffness: 300, damping: 16, delay: 0.1 }} className="relative mx-auto h-20 w-20 rounded-full bg-teal-600 text-white flex items-center justify-center shadow-xl shadow-teal-600/40"><PartyPopper className="h-10 w-10" /></motion.span>
      <h1 className="relative text-3xl sm:text-4xl font-extrabold text-slate-900 mt-5">{title}</h1>
      <p className="relative text-slate-600 mt-2 leading-relaxed">{sub}</p>
      {reference && <p className="relative mt-4 inline-flex items-center gap-2 rounded-full bg-slate-900 text-white text-xs font-bold px-4 h-9" data-testid="estimate-reference">{t.reference}: {reference}</p>}
      {email && <p className="relative text-xs text-slate-500 mt-3 leading-relaxed" data-testid="estimate-confirmation-email"><Mail className="inline h-3.5 w-3.5 mr-1 -mt-0.5" />{t.esConfirmSent} <b className="text-slate-700 break-all">{email}</b></p>}
      <div className="relative mt-7"><MotionButton onClick={() => navigate('/project')} data-testid="view-my-projects-btn"><ClipboardCheck className="h-5 w-5" /> {t.viewProject}</MotionButton></div>
    </motion.div>
  );
};

export const EstimateStep = () => {
  const { t } = useT();
  const { project, reload } = useProject();
  const { customer } = useStudioAuth();
  const navigate = useNavigate();
  const lead = project.lead;
  const [f, setF] = useState({ address: lead.address || '', city: lead.city || '', zip_code: lead.zip_code || '', phone: customer.phone || '', best_time: lead.best_time || 'any', notes: '', year_built: lead.year_built || '' });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  if (done) return <Page testId="estimate-step"><Success reference={done.reference} email={done.confirmation_sent_to} title={t.esDoneTitle} sub={t.esDoneSub} /></Page>;
  if (lead.stage === 'estimate_requested') {
    return (
      <Page testId="estimate-step">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="st-glass-strong rounded-[2rem] p-8 text-center max-w-xl mx-auto" data-testid="estimate-already-requested">
          <span className="mx-auto h-16 w-16 rounded-full bg-teal-600 text-white flex items-center justify-center shadow-lg"><CheckCircle2 className="h-8 w-8" /></span>
          <h1 className="text-3xl font-extrabold text-slate-900 mt-4">{t.esDoneTitle}</h1>
          <p className="text-slate-600 mt-2">{t.alreadyRequested}</p>
          {lead.assigned_to_name && <p className="mt-3 text-sm font-bold text-teal-800">{t.estimateAssigned(lead.assigned_to_name)}</p>}
          <div className="mt-6 flex justify-center gap-3"><MotionButton variant="ghost" onClick={() => navigate('/project/reveal')} data-testid="estimate-back-to-reveal">{t.seeResult}</MotionButton><MotionButton onClick={() => navigate('/project')} data-testid="view-my-projects-btn">{t.viewProject}</MotionButton></div>
        </motion.div>
      </Page>
    );
  }

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await studioApi.post('/estimate', { ...f, year_built: f.year_built ? Number(f.year_built) : null, state: 'GA', photo_ids: project.photos.map((p) => p.id), public_base: window.location.origin });
      setDone(data); reload().catch(() => {});
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <Page testId="estimate-step">
      <motion.form onSubmit={submit} variants={stagger} initial="initial" animate="animate" className="max-w-2xl mx-auto space-y-6" data-testid="estimate-form-modal">
        <motion.div variants={rise} className="text-center">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900">{t.esTitle}</h1>
          <p className="text-slate-600 mt-2">{t.esSub}</p>
        </motion.div>
        <motion.div variants={rise} className="st-glass-strong rounded-[2rem] p-5 sm:p-8 space-y-4">
          <Field label={t.address} testId="input-address" value={f.address} onChange={set('address')} autoComplete="street-address" required />
          <div className="grid grid-cols-[1fr_8rem] gap-3">
            <Field label={t.city} testId="input-city" value={f.city} onChange={set('city')} autoComplete="address-level2" />
            <Field label={t.zip} testId="input-zip" value={f.zip_code} onChange={set('zip_code')} autoComplete="postal-code" inputMode="numeric" />
          </div>
          <div className="grid grid-cols-[1fr_9rem] gap-3">
            <Field label={t.phone.replace(/ \(.*\)$/, '')} testId="input-phone" type="tel" value={f.phone} onChange={set('phone')} autoComplete="tel" required />
            <Field label={t.yearBuilt} testId="input-year-built" type="number" min="1700" max={new Date().getFullYear() + 1} inputMode="numeric" placeholder="1985" value={f.year_built} onChange={set('year_built')} />
          </div>
          <div>
            <span className="text-xs font-bold text-slate-600 ml-1">{t.bestTime}</span>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-1" data-testid="select-contact-time">
              {Object.entries(t.times).map(([k, v]) => { const I = TIME_ICONS[k]; const on = f.best_time === k; return (
                <motion.button key={k} whileTap={{ scale: 0.95 }} type="button" onClick={() => setF({ ...f, best_time: k })} aria-pressed={on} className={`h-12 rounded-2xl border text-sm font-bold inline-flex items-center justify-center gap-1.5 transition-colors ${on ? 'bg-slate-900 text-white border-slate-900' : 'bg-white/80 text-slate-700 border-slate-200'}`} data-testid={`contact-time-${k}`}><I className="h-4 w-4" /> {v}</motion.button>
              ); })}
            </div>
          </div>
          <Field label={t.notes} testId="input-notes" as="textarea" value={f.notes} onChange={set('notes')} placeholder={t.notesPh} />
        </motion.div>
        <motion.div variants={rise} className="flex items-center justify-between gap-3">
          <button type="button" onClick={() => navigate('/project/reveal')} className="inline-flex items-center gap-1 text-sm font-bold text-slate-600 hover:text-slate-900" data-testid="estimate-back-btn"><ArrowLeft className="h-4 w-4" /> {t.back}</button>
          <MotionButton type="submit" disabled={busy} className="px-7" data-testid="submit-estimate-btn"><ClipboardCheck className="h-5 w-5" /> {busy ? t.submitting : t.submit}</MotionButton>
        </motion.div>
      </motion.form>
    </Page>
  );
};
